import React from 'react';
import { Button, Form, Input } from 'antd';

import { extractApiErrorMessage, openNotification, toTurkishUpper } from '../../../utils';
import { createState, getState, updateState } from '../services/stateService';
import type { CreateStateDto } from '../types/State';

type FormValues = {
  name: string;
  stateCode?: string;
};

const DEFAULT_COUNTRY_ID = 225;

const normalizeOptionalText = (value?: string | null) => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

export default function StateForm({ id, onSuccess }: { id?: number; onSuccess: () => void }) {
  const [form] = Form.useForm<FormValues>();
  const [countryId, setCountryId] = React.useState<number>(DEFAULT_COUNTRY_ID);

  React.useEffect(() => {
    const load = async () => {
      try {
        if (id) {
          const state = await getState(id);
          form.setFieldsValue({
            name: state?.name ?? '',
            stateCode: state?.stateCode ?? '',
          });
          setCountryId(typeof state?.countryId === 'number' ? state.countryId : DEFAULT_COUNTRY_ID);
        } else {
          form.resetFields();
          setCountryId(DEFAULT_COUNTRY_ID);
        }
      } catch (error) {
        const message = extractApiErrorMessage(error, 'İl formu yüklenemedi');
        openNotification('error', 'Hata', message);
      }
    };

    load();
  }, [id, form]);

  const onFinish = async (values: FormValues) => {
    const payload: CreateStateDto = {
      name: toTurkishUpper(values.name)?.trim() ?? '',
      countryId,
      stateCode: normalizeOptionalText(values.stateCode),
    };

    try {
      if (id) {
        await updateState(id, payload);
        openNotification('success', 'Başarılı', 'İl güncellendi');
      } else {
        await createState(payload);
        openNotification('success', 'Başarılı', 'İl eklendi');
      }
      onSuccess();
    } catch (error) {
      const message = extractApiErrorMessage(error, 'İl işlemi başarısız');
      openNotification('error', 'Hata', message);
    }
  };

  return (
    <Form form={form} layout='vertical' onFinish={onFinish}>
      <Form.Item name='stateCode' label='İl Kodu'>
        <Input />
      </Form.Item>
      <Form.Item name='name' label='İl' rules={[{ required: true, message: 'İl zorunludur' }]}>
        <Input style={{ textTransform: 'uppercase' }} lang='tr' />
      </Form.Item>
      <Button htmlType='submit' type='primary' block>
        Kaydet
      </Button>
    </Form>
  );
}
